import React, { Suspense, useState } from 'react'

import ProfilePhotoFallback from 'src/shared/assets/images/profile-avatar-fallback.png'
import { ErrorBoundaryWrapper } from 'src/shared/components/errors/error-boundary-wrapper'
import { Avatar, AvatarImage } from 'src/shared/components/ui/miscellaneous/avatar'
import { Skeleton } from 'src/shared/components/ui/miscellaneous/skeleton'
import { cn } from 'src/shared/utils/cn'

import ProfileAvatarDialog from './profile-avatar-dialog'

interface ProfileAvatarProps {
  photoUrl?: string
  size?: number
  className?: string
  onClick?: () => void
  /** If true, a popup with a larger version of the avatar will be shown when the avatar is clicked. */
  shouldZoom?: boolean
}

export { ProfilePhotoFallback }

const ProfileAvatarContent: React.FC<ProfileAvatarProps> = ({
  photoUrl,
  size = 40,
  className,
  onClick,
  shouldZoom = false,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const handleClick = () => {
    if (shouldZoom) {
      setIsDialogOpen(true)
    }
    if (onClick) {
      onClick()
    }
  }

  if (!photoUrl) {
    photoUrl = ProfilePhotoFallback
  }

  const imageStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
  }

  return (
    <>
      <Avatar
        style={{ height: size, width: size }}
        className={cn(`rounded-full ${className || ''} overflow-hidden bg-accent`, shouldZoom && 'cursor-pointer')}
        onClick={handleClick}
      >
        <AvatarImage src={photoUrl} alt={'user profile photo'} style={imageStyle} className="object-cover" />
      </Avatar>
      {shouldZoom && <ProfileAvatarDialog isOpen={isDialogOpen} setOpen={setIsDialogOpen} photo={photoUrl} />}
    </>
  )
}

export const ProfileAvatarURL: React.FC<ProfileAvatarProps> = ({
  photoUrl,
  size = 40,
  className,
  onClick,
  shouldZoom = false,
}) => {
  return (
    <ErrorBoundaryWrapper style="custom" fallback={<Skeleton className="size-4 rounded-full bg-accent" />}>
      <Suspense fallback={<Skeleton className="size-4 rounded-full bg-accent" />}>
        <ProfileAvatarContent
          photoUrl={photoUrl}
          size={size}
          className={className}
          onClick={onClick}
          shouldZoom={shouldZoom}
        />
      </Suspense>
    </ErrorBoundaryWrapper>
  )
}

export default ProfileAvatarURL
